import { Engineering, HomeWork, LocalShipping, LocationOn, SvgIconComponent, Warehouse } from '@mui/icons-material';
import { FeatureCollection, Point } from 'geojson';
import { MARKER_TYPES, Marker, MarkerType } from '@/map/domain/entities/map.types';

import { Theme } from '@mui/material/styles';

// Marker styling utilities
export const getMarkerColor = (type: MarkerType, theme: Theme): string => {
  switch (type) {
    case MARKER_TYPES.JOBSITE:
      return theme.palette.error.main;
    case MARKER_TYPES.YARDS:
      return theme.palette.warning.main;
    case MARKER_TYPES.EQUIPMENTS:
      return theme.palette.primary.main;
    case MARKER_TYPES.EMPLOYEES:
      return theme.palette.success.dark;
    default:
      return theme.palette.grey[500];
  }
};

export const getMarkerIcon = (type: MarkerType): SvgIconComponent => {
  switch (type) {
    case MARKER_TYPES.JOBSITE:
      return HomeWork;
    case MARKER_TYPES.YARDS:
      return Warehouse;
    case MARKER_TYPES.EQUIPMENTS:
      return LocalShipping;
    case MARKER_TYPES.EMPLOYEES:
      return Engineering;
    default:
      return LocationOn;
  }
};

export const getMarkerZIndex = (type: MarkerType): number => {
  switch (type) {
    case MARKER_TYPES.JOBSITE:
      return 3;
    case MARKER_TYPES.YARDS:
      return 2;
    case MARKER_TYPES.EQUIPMENTS:
      return 1;
    case MARKER_TYPES.EMPLOYEES:
      return 4;
    default:
      return 0;
  }
};

// Cluster utilities
export const getSortedTypesByCount = (types: MarkerType[]): MarkerType[] => {
  const typeCounts = types.reduce(
    (acc, type) => {
      acc[type] = (acc[type] ?? 0) + 1;
      return acc;
    },
    {} as Record<MarkerType, number>
  );
  return Object.entries(typeCounts)
    .sort(([, a], [, b]) => b - a)
    .map(([type]) => type as MarkerType);
};

export const getBorderSegments = (types: MarkerType[], theme: Theme): string => {
  if (!types.length) return '';

  const typeCounts = types.reduce(
    (acc, type) => {
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    },
    {} as Record<MarkerType, number>
  );

  const total = types.length;
  let currentPercent = 0;
  const sortedTypes = Object.entries(typeCounts)
    .sort(([, a], [, b]) => b - a)
    .map(([type]) => type as MarkerType);

  const segments = sortedTypes.map((type) => {
    const count = typeCounts[type];
    const percent = (count / total) * 100;
    const color = getMarkerColor(type, theme);
    const start = currentPercent.toFixed(2);
    currentPercent += percent;
    const end = currentPercent.toFixed(2);
    return `${color} ${start}% ${end}%`;
  });

  if (segments.length > 0) {
    segments[segments.length - 1] = segments[segments.length - 1]?.replace(/\d+\.\d+%$/, '100%') ?? '';
  }

  return segments.join(', ');
};

// GeoJSON marker properties
interface MarkerProperties {
  title: string;
  draggable?: boolean;
  zIndex?: number;
  type: MarkerType;
  label?: string;
}

// GeoJSON conversion
export const markersToGeoJSON = (markers: Marker[]): FeatureCollection<Point, MarkerProperties> => ({
  type: 'FeatureCollection',
  features: markers.map((marker) => ({
    type: 'Feature',
    id: marker.id,
    properties: {
      title: marker.title ?? '',
      draggable: marker.draggable,
      zIndex: marker.zIndex,
      type: marker.type,
      label: marker.label ?? ''
    },
    geometry: {
      type: 'Point',
      coordinates: [marker.position.lng, marker.position.lat]
    }
  }))
});

export const getValidMarkerType = (type: string | undefined): MarkerType => {
  return type && Object.values(MARKER_TYPES).includes(type as MarkerType) ? (type as MarkerType) : MARKER_TYPES.JOBSITE;
};
